import React from 'react';
import { Loader2, Terminal, FileCode, Search, Package, Play, Rocket, AlertCircle, Brain } from 'lucide-react';
import type { AgentStep } from '@/stores/useAIChatStore';

const TOOL_ICONS: Record<string, React.ReactNode> = {
  'write-file': <FileCode className="h-3.5 w-3.5 text-blue-400" />,
  'read-file': <FileCode className="h-3.5 w-3.5 text-green-400" />,
  'run-command': <Terminal className="h-3.5 w-3.5 text-yellow-400" />,
  'list-files': <Search className="h-3.5 w-3.5 text-purple-400" />,
  'search-files': <Search className="h-3.5 w-3.5 text-purple-400" />,
  'install-package': <Package className="h-3.5 w-3.5 text-orange-400" />,
  'preview': <Play className="h-3.5 w-3.5 text-cyan-400" />,
  'deploy': <Rocket className="h-3.5 w-3.5 text-pink-400" />,
};

interface AgentStepRendererProps {
  steps: AgentStep[];
}

export default function AgentStepRenderer({ steps }: AgentStepRendererProps) {
  if (steps.length === 0) return null;

  return (
    <div className="space-y-1.5">
      {steps.map((step) => (
        <StepItem key={step.id} step={step} />
      ))}
    </div>
  );
}

function StepItem({ step }: { step: AgentStep }) {
  switch (step.type) {
    case 'thinking':
      return (
        <div className="flex items-start gap-2 text-xs text-muted-foreground py-1 px-2 bg-muted/30 rounded">
          <Brain className="h-3.5 w-3.5 mt-0.5 shrink-0 text-violet-400" />
          <span className="italic">{step.data.content || 'Thinking...'}</span>
        </div>
      );

    case 'tool_call_start':
      return (
        <div className="flex items-start gap-2 text-xs py-1 px-2 bg-muted/50 rounded border border-border/50">
          {TOOL_ICONS[step.data.tool] || <Terminal className="h-3.5 w-3.5 text-muted-foreground" />}
          <div className="min-w-0 flex-1">
            <span className="font-medium text-foreground">{step.data.tool}</span>
            {step.data.args && (
              <pre className="mt-1 text-[11px] text-muted-foreground overflow-x-auto whitespace-pre-wrap break-all">
                {typeof step.data.args === 'string' ? step.data.args : JSON.stringify(step.data.args, null, 2)}
              </pre>
            )}
          </div>
          <Loader2 className="h-3 w-3 animate-spin text-muted-foreground shrink-0 mt-0.5" />
        </div>
      );

    case 'tool_call_result':
      return (
        <div className="text-xs py-1 px-2 bg-muted/30 rounded border-l-2 border-green-500/50">
          <div className="flex items-center gap-1.5 mb-1">
            <span className="text-green-500 font-medium">Result</span>
            {step.data.tool && <span className="text-muted-foreground">({step.data.tool})</span>}
          </div>
          {step.data.result && (
            <pre className="text-[11px] text-muted-foreground overflow-x-auto whitespace-pre-wrap break-all max-h-[120px] overflow-y-auto">
              {typeof step.data.result === 'string'
                ? step.data.result.slice(0, 500)
                : JSON.stringify(step.data.result, null, 2).slice(0, 500)}
            </pre>
          )}
        </div>
      );

    case 'file_change':
      return (
        <div className="flex items-center gap-2 text-xs py-1 px-2 bg-blue-500/10 rounded border-l-2 border-blue-500/50">
          <FileCode className="h-3.5 w-3.5 text-blue-400" />
          <span className="text-blue-400 font-medium">File updated:</span>
          <span className="text-foreground font-mono">{step.data.path}</span>
        </div>
      );

    case 'error':
      return (
        <div className="flex items-start gap-2 text-xs py-1 px-2 bg-red-500/10 rounded border-l-2 border-red-500/50">
          <AlertCircle className="h-3.5 w-3.5 text-red-400 shrink-0 mt-0.5" />
          <span className="text-red-400">{step.data.message || step.data.error || 'Unknown error'}</span>
        </div>
      );

    case 'text':
      return null; // Text is rendered as part of the assistant message content

    default:
      return null;
  }
}
